import * as age from "age-encryption";
import fs from "fs/promises";
import path from "path";

const DEFAULT_IDENTITY_PATH = path.resolve(process.cwd(), ".envfortress.key");
const ENV_VAR = "ENVFORTRESS_PRIVATE_KEY";

/**
 * Generate a new age identity and store it using the selected backend.
 * @param options { secureStore: 'file' | 'env', path?: string }
 */
export async function generateAndStoreIdentity(options: {
  secureStore: "file" | "env";
  path?: string;
}) {
  const identity = await age.generateIdentity();
  if (options.secureStore === "file") {
    const filePath = options.path || DEFAULT_IDENTITY_PATH;
    await fs.writeFile(filePath, identity, { encoding: "utf-8", mode: 0o600 });
    return filePath;
  } else if (options.secureStore === "env") {
    // For env, instruct user to set ENVFORTRESS_PRIVATE_KEY
    process.env[ENV_VAR] = identity;
    return ENV_VAR;
  } else {
    throw new Error("Unsupported secureStore backend");
  }
}

/**
 * Export the stored identity to a file or stdout.
 * @param options { from: 'file' | 'env', path?: string, output?: string }
 */
export async function exportIdentity(options: {
  from: "file" | "env";
  path?: string;
  output?: string;
}) {
  let identity: string;
  if (options.from === "file") {
    const filePath = options.path || DEFAULT_IDENTITY_PATH;
    identity = await fs.readFile(filePath, "utf-8");
  } else if (options.from === "env") {
    identity = process.env[ENV_VAR] || "";
    if (!identity)
      throw new Error("No identity found in ENVFORTRESS_PRIVATE_KEY");
  } else {
    throw new Error("Unsupported source");
  }
  if (options.output) {
    await fs.writeFile(options.output, identity, {
      encoding: "utf-8",
      mode: 0o600,
    });
  } else {
    console.log(identity);
  }
}

/**
 * Import an identity from a file or string and store it using the selected backend.
 * @param options { secureStore: 'file' | 'env', input?: string, path?: string }
 */
export async function importIdentity(options: {
  secureStore: "file" | "env";
  input?: string;
  path?: string;
}) {
  let identity: string;
  if (options.input) {
    identity = options.input;
  } else {
    throw new Error("No input provided for identity import");
  }
  if (options.secureStore === "file") {
    const filePath = options.path || DEFAULT_IDENTITY_PATH;
    await fs.writeFile(filePath, identity, { encoding: "utf-8", mode: 0o600 });
    return filePath;
  } else if (options.secureStore === "env") {
    process.env[ENV_VAR] = identity;
    return ENV_VAR;
  } else {
    throw new Error("Unsupported secureStore backend");
  }
}

// TODO: Add OS keychain support in the future
